import { useCallback, useEffect, useState } from 'react';
import { useStore } from '../../../store';
import { useChangeNaicsMutation } from './useChangeNaicsMutation';
import { useGetIndustries } from './useGetIndustries';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { ChangeNaicsProps } from '../ChangeNaics';
import { useAccountSummaryQuery } from '../../../hooks/query/useAccountSummaryQuery';
import { IFlattenServerErrorResponse } from '../../../types';

export interface Industry {
  industry: string;
  subIndustryList: string[];
}

interface UseChangeNaicsReturn {
  errorMessage: string | undefined;
  isLoading: boolean;
  onSubmit: () => void;
  onIndustriesChanged: (industry: string, subIndustry: string) => void;
  isValid: boolean;
  industryList: Industry[];
}

export const useChangeNaicsHook = ({ onNaicsUpdated }: ChangeNaicsProps): UseChangeNaicsReturn => {
  const { customerId } = useStore((state) => state.currUser());
  const [newIndustries, setNewIndustries] = useState({ industry: '', subIndustry: '' });
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isValid, setIsValid] = useState(false);
  const { isLoading: isLoadingIndustries, data: industryList } = useGetIndustries({ customerId }, (e: IFlattenServerErrorResponse) => { setErrorMessage(e.localizedMessage); });

  const { refetch: updateSummary, isRefetching: isLoadingSummary } = useAccountSummaryQuery(() => {
    onNaicsUpdated();
  });

  useEffect(() => {
    trackMixPanel('Change Naics modal showed');
  }, []);

  const onSuccessfulNaicsUpdate = useCallback(() => {
    trackMixPanel('Change Naics updated successfully');
    updateSummary();
  }, [onNaicsUpdated]);

  const onFailedNaicsUpdate = useCallback((error: IFlattenServerErrorResponse) => {
    trackMixPanel('Change Naics update failed', { errorCode: error.code });
    setErrorMessage(error.localizedMessage);
  }, []);

  const { isLoading: isLoadingChangeNaics, mutate: mutateChangeNaics } = useChangeNaicsMutation({ customerId }, onSuccessfulNaicsUpdate, onFailedNaicsUpdate);

  const onIndustriesChanged = useCallback((industry: string, subIndustry: string) => {
    setNewIndustries({ industry, subIndustry });
    setErrorMessage(undefined);
    if (!industry || !subIndustry) {
      setIsValid(false);
    } else {
      setIsValid(true);
    }
  }, []);

  const onSubmit = useCallback(() => {
    trackMixPanel('Change Naics submit clicked');
    setErrorMessage(undefined);
    mutateChangeNaics({ segment: newIndustries.industry, naicsIndustry: newIndustries.subIndustry });
  }, [newIndustries, mutateChangeNaics]);

  return {
    errorMessage,
    isLoading: isLoadingIndustries || isLoadingChangeNaics || isLoadingSummary,
    onSubmit,
    onIndustriesChanged,
    isValid,
    industryList,
  };
};
